import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Image as ImageIcon, Upload, Copy, Trash2, AlertTriangle, Search, FileText,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  // Build the public URL using the frontend's known backend URL.
  // We don't trust the absolute URL the backend returns because Kubernetes
  // ingress can rewrite the Host header — REACT_APP_BACKEND_URL is canonical.
  const fullUrl = (item) => {
    if (item.relative_url) return `${API_URL}${item.relative_url}`;
    return item.url;
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/media/list`, { headers: auth() });
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load media');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);

    let successCount = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API_URL}/api/admin/media/upload`, fd, {
          headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
        });
        successCount += 1;
      } catch (e) {
        const msg = e.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(msg);
      }
    }
    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
      fetchItems();
    }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = ''; // allow re-uploading same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/')));
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/media/${itemToDelete.id}`, { headers: auth() });
      toast.success('Deleted');
      setItemToDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = search.trim()
    ? items.filter((m) => (m.original_name || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-1">Media Library</h1>
        <p className="text-gray-400">
          Upload images and copy their URLs to use in emails, the storefront, or anywhere else.
        </p>
      </div>

      {/* Upload zone */}
      <Card
        className={`bg-gray-800 border-2 border-dashed transition-colors ${
          dragOver ? 'border-blue-500 bg-blue-500/5' : 'border-gray-700 hover:border-gray-600'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <CardContent className="p-10 text-center">
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/jpeg,image/png,image/webp,image/svg+xml,image/gif"
            className="hidden"
            onChange={handleFileInput}
            data-testid="media-file-input"
          />
          <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">
            {uploading ? 'Uploading...' : dragOver ? 'Drop to upload' : 'Drag images here or click to browse'}
          </p>
          <p className="text-xs text-gray-500 mb-4">
            JPEG, PNG, WEBP, SVG, GIF · up to 5 MB each
          </p>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            disabled={uploading}
            data-testid="media-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Select files'}
          </Button>
        </CardContent>
      </Card>

      {/* Search */}
      {items.length > 0 && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by filename..."
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            data-testid="media-search"
          />
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <p className="text-gray-400">Loading media...</p>
      ) : filtered.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-12 text-center">
            <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              {items.length === 0
                ? 'No images uploaded yet. Upload your first one above.'
                : 'No images match your search.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((item) => (
            <Card key={item.id} className="bg-gray-800 border-gray-700 overflow-hidden group" data-testid="media-item">
              <div className="aspect-square bg-gray-900 flex items-center justify-center overflow-hidden">
                {item.mime_type?.startsWith('image/') ? (
                  <img
                    src={fullUrl(item)}
                    alt={item.alt_text || item.original_name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <FileText className="w-12 h-12 text-gray-500" />
                )}
              </div>
              <CardContent className="p-3 space-y-2">
                <p className="text-sm text-white truncate" title={item.original_name}>
                  {item.original_name}
                </p>
                <p className="text-xs text-gray-500">{formatBytes(item.size_bytes)}</p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => copyUrl(fullUrl(item))}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                    data-testid={`copy-url-${item.id}`}
                  >
                    <Copy className="w-3 h-3" />
                    Copy URL
                  </button>
                  <button
                    onClick={() => setItemToDelete(item)}
                    className="px-2 py-1.5 text-xs bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded transition-colors"
                    title="Delete"
                    data-testid={`delete-media-${item.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      {itemToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-800 border-red-500/40 max-w-md w-full">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <h2 className="text-xl font-bold text-white">Delete this image?</h2>
              </div>
              <p className="text-gray-300 text-sm">
                <strong className="text-red-400">This cannot be undone.</strong> If this image's URL is being used somewhere
                (an email template, settings, etc.), it will become a broken link.
              </p>
              <div className="bg-gray-900 rounded p-3 text-sm flex items-center gap-3">
                {itemToDelete.mime_type?.startsWith('image/') && (
                  <img src={fullUrl(itemToDelete)} alt="" className="w-12 h-12 object-cover rounded" />
                )}
                <div className="min-w-0">
                  <p className="text-white truncate">{itemToDelete.original_name}</p>
                  <p className="text-xs text-gray-500">{formatBytes(itemToDelete.size_bytes)}</p>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setItemToDelete(null)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid="confirm-delete-media-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
